import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Breadcrumb, Spinner } from '../../../shared/components';
import { ProductAttributeDefinition, ProductCategory } from '../types/Product';
import { createProduct, getProductCategories, productService } from '../services/productService';
import { CategorySelect } from '../../catalog/CategorySelect';
import { ProductAttributeFields } from './ProductAttributeFields';
import { useTenantCurrency } from '../../invoices/tenantCurrency';
import styles from '../../catalog/Catalog.module.css';

const parseNumber = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const AddProduct: React.FC = () => {
  const { t } = useTranslation(['products', 'common']);
  const navigate = useNavigate();
  const currency = useTenantCurrency();
  const [categories, setCategories] = React.useState<ProductCategory[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [generalError, setGeneralError] = React.useState<string | null>(null);
  const [form, setForm] = React.useState({
    name: '',
    displayName: '',
    description: '',
    categoryId: '',
    code: '',
    barcode: '',
    costPrice: '',
    markupPercent: '',
    salePrice: '',
    taxPercent: '',
    stockQuantity: '0',
    unit: '',
  });
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [definitions, setDefinitions] = React.useState<ProductAttributeDefinition[]>([]);
  const [attributes, setAttributes] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await getProductCategories();
        setCategories(data);
        if (data.length === 1) {
          setForm(prev => ({ ...prev, categoryId: data[0].id }));
        }
      } catch {
        setGeneralError(t('failedToLoadCategories', { ns: 'products' }));
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [t]);

  React.useEffect(() => {
    if (!form.categoryId) {
      setDefinitions([]);
      setAttributes({});
      return;
    }

    let cancelled = false;
    void productService.getAttributes(form.categoryId, true)
      .then(items => {
        if (cancelled) return;
        setDefinitions(items);
        setAttributes(prev => {
          const next: Record<string, string> = {};
          items.forEach(item => {
            if (prev[item.id]) next[item.id] = prev[item.id];
          });
          return next;
        });
      })
      .catch(() => {
        if (!cancelled) {
          setDefinitions([]);
          setGeneralError(t('failedToLoadAttributes', { ns: 'products' }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [form.categoryId, t]);

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (!form.name.trim()) nextErrors.name = t('nameRequired', { ns: 'products' });
    if (!form.categoryId) nextErrors.categoryId = t('categoryRequired', { ns: 'products' });
    definitions.forEach(definition => {
      if (definition.isRequired && !(attributes[definition.id] || '').trim()) {
        nextErrors[definition.id] = t('attributeRequired', { ns: 'products', name: definition.name });
      }
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setGeneralError(null);
    try {
      const created = await createProduct({
        name: form.name.trim(),
        displayName: form.displayName.trim() || undefined,
        description: form.description.trim() || undefined,
        categoryId: form.categoryId,
        code: form.code.trim() || undefined,
        barcode: form.barcode.trim() || undefined,
        costPrice: parseNumber(form.costPrice),
        markupPercent: parseNumber(form.markupPercent),
        salePrice: parseNumber(form.salePrice),
        taxPercent: parseNumber(form.taxPercent),
        stockQuantity: parseNumber(form.stockQuantity) ?? 0,
        unit: form.unit.trim() || undefined,
        attributes: Object.fromEntries(definitions.map(item => [item.id, attributes[item.id]?.trim() || null])),
      });
      if (!created) {
        setGeneralError(t('failedToSave', { ns: 'products' }));
        return;
      }
      navigate(`/products/${created.id}`);
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToSave', { ns: 'products' }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}><Spinner text={t('loading', { ns: 'products' })} /></div>;
  }

  return (
    <div className={styles.page}>
      <Breadcrumb items={[
        { label: t('title', { ns: 'products' }), path: '/products' },
        { label: t('addTitle', { ns: 'products' }), path: '/products/add' },
      ]} />
      <h2 className={styles.title}>{t('addTitle', { ns: 'products' })}</h2>
      {categories.length === 0 ? <div className="error">{t('createCategoryFirst', { ns: 'products' })}</div> : null}
      {generalError ? <div className="error">{generalError}</div> : null}

      <form className={styles.form} onSubmit={onSubmit}>
        <div className={styles.field}>
          <label className={styles.label}>{t('name', { ns: 'products' })}</label>
          <input className={styles.input} value={form.name} placeholder={t('namePlaceholder', { ns: 'products' })} onChange={event => setForm(prev => ({ ...prev, name: event.target.value }))} />
          {errors.name ? <div className={styles.fieldError}>{errors.name}</div> : null}
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('displayName', { ns: 'products' })}</label>
          <input className={styles.input} value={form.displayName} onChange={event => setForm(prev => ({ ...prev, displayName: event.target.value }))} />
        </div>
        <div className={`${styles.field} ${styles.fieldFull}`}>
          <label className={styles.label}>{t('description', { ns: 'products' })}</label>
          <textarea className={styles.textarea} value={form.description} onChange={event => setForm(prev => ({ ...prev, description: event.target.value }))} />
          <div className={styles.fieldHint}>{t('descriptionHint', { ns: 'products' })}</div>
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('category', { ns: 'products' })}</label>
          <CategorySelect
            className={styles.input}
            categories={categories}
            value={form.categoryId}
            onChange={categoryId => setForm(prev => ({ ...prev, categoryId }))}
            emptyLabel={t('selectCategory', { ns: 'products' })}
          />
          <div className={styles.fieldHint}>{t('categoryHint', { ns: 'products' })}</div>
          {errors.categoryId ? <div className={styles.fieldError}>{errors.categoryId}</div> : null}
        </div>
        <ProductAttributeFields
          definitions={definitions}
          values={attributes}
          errors={errors}
          onChange={(attributeId, value) => setAttributes(prev => ({ ...prev, [attributeId]: value }))}
        />
        <div className={styles.field}>
          <label className={styles.label}>{t('code', { ns: 'products' })}</label>
          <input className={styles.input} value={form.code} onChange={event => setForm(prev => ({ ...prev, code: event.target.value }))} />
          <div className={styles.fieldHint}>{t('codeHint', { ns: 'products' })}</div>
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('barcode', { ns: 'products' })}</label>
          <input className={styles.input} value={form.barcode} onChange={event => setForm(prev => ({ ...prev, barcode: event.target.value }))} />
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('unit', { ns: 'products' })}</label>
          <input className={styles.input} value={form.unit} placeholder={t('unitPlaceholder', { ns: 'products' })} onChange={event => setForm(prev => ({ ...prev, unit: event.target.value }))} />
          <div className={styles.fieldHint}>{t('unitHint', { ns: 'products' })}</div>
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('costPrice', { ns: 'products' })} ({currency})</label>
          <input className={styles.input} type="number" min="0" step="0.01" value={form.costPrice} onChange={event => setForm(prev => ({ ...prev, costPrice: event.target.value }))} />
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('markupPercent', { ns: 'products' })}</label>
          <input className={styles.input} type="number" min="0" step="0.01" value={form.markupPercent} onChange={event => setForm(prev => ({ ...prev, markupPercent: event.target.value }))} />
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('salePrice', { ns: 'products' })} ({currency})</label>
          <input className={styles.input} type="number" min="0" step="0.01" value={form.salePrice} onChange={event => setForm(prev => ({ ...prev, salePrice: event.target.value }))} />
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('taxPercent', { ns: 'products' })}</label>
          <input className={styles.input} type="number" min="0" max="100" step="0.01" value={form.taxPercent} onChange={event => setForm(prev => ({ ...prev, taxPercent: event.target.value }))} />
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('stockQuantity', { ns: 'products' })}</label>
          <input className={styles.input} type="number" min="0" step="1" value={form.stockQuantity} onChange={event => setForm(prev => ({ ...prev, stockQuantity: event.target.value }))} />
        </div>
        <div className={styles.actions}>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/products')} disabled={saving}>{t('back', { ns: 'products' })}</button>
          <button type="submit" className="btn btn-primary" disabled={saving || categories.length === 0}>{t('save', { ns: 'products' })}</button>
        </div>
      </form>
      <p className={styles.fieldHint}>{t('photoAfterSave', { ns: 'products' })}</p>
    </div>
  );
};

export default AddProduct;
